import { html, css } from "lit";
import { customElement, property, query, queryAll } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { ContextAxis } from "@reporting/rp-types/analytics";
import { DropdownList, Input, OmegaComponent, Switch, events } from "@omegagrid/core";

@customElement('rp-fsection-axis-settings')
export class AxisSettingsSection extends OmegaComponent {

	static styles = [OmegaComponent.styles, css`
		:host {
			padding-top: 4px;
		}

		.row {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			width: 180px;
			margin-bottom: 4px;
		}

		.col-1, .col-2 {
			padding: 0 5px;
		}

		.col-1 {
			flex: 0 0 65px;
			white-space: nowrap;
		}

		.col-2 {
			flex: 1;
			overflow: hidden;
		}

		og-switch {
			height: 18px;
			width: 30px;
		}

		og-dropdown-list, og-input {
			width: 100%;
		}
	`];

	@property({type: Object})
	axis: ContextAxis;

	@property({type: Number})
	axisIndex: number;

	@queryAll('.field')
	fields: NodeListOf<HTMLElement>;

	updateAxis() {
		this.fields.forEach(field => {
			const key = field.dataset.name as keyof ContextAxis;
			let value: any;
			if (field instanceof Switch) {
				value = field.state == 'on';
			} else if (field instanceof Input) {
				value = field.value || undefined;
			} else if (field instanceof DropdownList) {
				value = field.value;
			} else {
				value = null;
			}

			// the level dropdown returns a string key, '0' stands for <All>
			if (key == 'level') value = parseInt(value) || undefined;

			(this.axis[key] as any) = value;
		});

		this.dispatchEvent(new events.ChangeEvent({value: this.axis}));
	}

	updated() {
		setTimeout(() => this.requestUpdate(), 1); // TODO: odd bug causing fields to not update properly
	}

	render = () => this.axis ? html`
		<div class="row">
			<div class="col-1">${msg('Alias')}</div>
			<div class="col-2">
				<og-input
					class="field"
					data-name="name"
					.value="${this.axis.name || ''}"
					@change="${() => this.updateAxis()}">
				</og-input>
			</div>
		</div>

		<div class="row">
			<div class="col-1">${msg('Level')}</div>
			<div class="col-2">
				<og-dropdown-list
					@change="${() => this.updateAxis()}"
					class="field"
					data-name="level"
					.value="${this.axis.level || '0'}"
					.items="${[
						{key: '0', value: `<${msg('All')}>`},
						...([...(new Array(10).keys())].map(i => ({key: (i + 1).toString(), value: (i + 1).toString()})))
					]}">
				</og-dropdown-list>
			</div>
		</div>

		<div class="row">
			<div class="col-1">${msg('Subtotal')}</div>
			<div class="col-2" style="text-align: right">
				<og-switch
					class="field"
					data-name="subtotal"
					shape="round"
					.state="${this.axis.subtotal ? 'on' : 'off'}"
					@change="${() => this.updateAxis()}">
				</og-switch>
			</div>
		</div>

		${this.axisIndex === 1 ? html`
			<div class="row">
				<div class="col-1"><og-label .title="${msg('Following level will be displayed in a new column')}" .text="${msg('Column Break')}" style="border-bottom: 1px dotted var(--og-text-color-3)"></og-label></div>
				<div class="col-2" style="text-align: right">
					<og-switch
						class="field"
						data-name="column_break"
						shape="round"
						.state="${this.axis.column_break ? 'on' : 'off'}"
						@change="${() => this.updateAxis()}">
					</og-switch>
				</div>
			</div>
		` : ''}
	` : '';

}
